import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Chip, CircularProgress, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { DeleteOutline, TuneOutlined } from '@mui/icons-material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import AlertUI from '../../generic/AlertUI'
import BomTreeView from '../workflow2.0/BomTreeView'
import { buildBomPositionTree } from '../workflow2.0/bomTree'
import { bomLineTypeLabelKey, bomLinesFooter, sortBomLines } from './bomLinePresentation'
import { useWorkflowResourceExtendedColumns } from '../workflow2.0/ResourcesExtendedColumnsManagement'
import { countDescendants, countLevelsBelow, collectSubtreeUuids, filterStructureBySearch } from './bomStructureStats'
import { buildBomVersionSaveRequestComponents, findRowsMissingRequiredFields } from './bomVersionSaveRequest'
import { apiGet } from '../../generic/Api_Functions'
import { getBomVersionLineUsage, getBomVersionSaveMode, getBomVersionStructure, readBomLibraryError, saveBomVersion } from './bomLibraryApi'
import { translateBomLibraryError } from './bomLibraryErrors'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import CatalogComponentPicker from './CatalogComponentPicker'
import BomSaveModeDialog from './BomSaveModeDialog'
import useBomLibraryIndexSetting, { DEFAULT_INDEX_VALUE_WITHOUT_INDEX } from './useBomLibraryIndexSetting'

const resourceCodeOf = (row) => (row.resource && row.resource.code) || row.resourceCode || ''
const resourceNameOf = (row) => (row.resource && row.resource.name) || row.resourceName || ''

// SmartMateriaUITable's multi-edit locates the edited row by `id` (`changeTextValue` /
// `changeEditedSelect`): a row without one would share every keystroke with every other id-less
// row, so each row is guaranteed a distinct `id` (its `uuid` when Sun did not send one).
const compareByCode = (one, two) => resourceCodeOf(one).localeCompare(resourceCodeOf(two), undefined, { numeric: true, sensitivity: 'base' })
/** Default table order: by component code (the SmartMateriaUITable sorts `resource.code` itself). */
export const DEFAULT_TABLE_SORT_OPTIONS = { default: { field: 'resource.code', direction: 1 } }

/** A promise the structure load settles by hand: the table's fetch waits on it. */
const arrival = () => {
  let settle
  const promise = new Promise((resolve) => { settle = resolve })
  return { promise, settle }
}
const withRowId = (row) => (row.id === undefined || row.id === null ? { ...row, id: row.uuid } : row)

/**
 * BomVersionStructure — structure of one BOM version, table/tree switchable (S30, historia 117
 * HU-03), built from the same pieces the workflow editor uses for its BOM:
 * - table: `SmartMateriaUITable` with the editor's BOM columns (engineering item/revision, type,
 *   code, name, position, revision, category, component type, quantity and the `workflow_resource`
 *   extended columns); with BOM_LIBRARY_EDIT every editable cell is open at once (`setMultiEdit`), so
 *   the fields Sun requires on save (`BomComponentInputDto`) can all be filled in;
 * - tree: `workflow2.0/BomTreeView` (position hierarchy, same row look), without job chips.
 * The "Search component" field FILTERS both views (S32): the table lists the matching components,
 * the tree shows them inside their branch (ancestors kept). Selecting a table row reports its
 * recursive descendant count and levels below (S31). Adding is catalog-only (S33), deleting takes
 * the subtree after a confirmation with the count (S36), and without BOM_LIBRARY_EDIT no editing
 * control is rendered (S38). Save feedback (validation refusal, Sun rejection, success) is an
 * `AlertUI` at the top of the section.
 *
 * @param {object} props
 * @param {number} props.bomId Version whose structure is shown
 * @param {{name: string, description: string, indexValue: string}} [props.version] Header of that
 * version: N9 needs `name` (E-BOM-02) and an `indexValue`, so the save request is built from it
 * @param {function(object)} [props.onSaved] Called with the `BomVersionSaveResult` after N9 succeeds
 */
/** A figure with its label as one outlined chip: bold tabular number, muted word. */
const StatChip = ({ value, label }) => (
  <Chip
    size='small'
    variant='outlined'
    label={(
      <>
        <Box component='span' sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
        {' ' + label}
      </>
    )}
  />
)

const BomVersionStructure = ({ bomId, version = {}, onSaved = () => {}, readOnly = false }) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  // H134 S54/S56 — ONE gate for every editing affordance. `readOnly` arrives true for an APPROVED
  // version (H134 S53) and the permission check covers the missing-BOM_LIBRARY_EDIT case (H134 S56):
  // same effect, different reason. Folding both into `canEdit` is what makes the affordances not
  // merely disabled but ABSENT, since every one of them already hangs off this flag.
  const canEdit = isUserAuthorized('BOM_LIBRARY_EDIT') && !readOnly
  const { withExtendedColumns } = useWorkflowResourceExtendedColumns(t_)
  // Feedback of the save (validation refused, N9 rejected or succeeded) at the top of the section,
  // the way the workflow editor reports its own saves.
  const [AlertElement, showAlert] = AlertUI()
  const [indexOptions, setIndexOptions] = useState([])
  // Business literal of a failed N9 (E-BOM-10), shown inside the save-mode dialog when it reopens.
  const [saveError, setSaveError] = useState('')

  // PO adjustment 2026-09-10 (RN-G20): with USE_WORKFLOW_INDEX off the index dimension does not
  // exist here — the catalogue is not even requested and the save dialog has no index selector.
  const { useIndex, loaded: indexSettingLoaded } = useBomLibraryIndexSetting()

  useEffect(() => {
    // `indexSettingLoaded` gates the call: `useIndex` optimistically starts as `true`, so asking
    // before the setting is known would request the catalogue the flag is meant to suppress.
    if (!canEdit || !indexSettingLoaded || !useIndex) return
    apiGet('indexValues/available').then((options) => setIndexOptions(options || []))
  }, [canEdit, indexSettingLoaded, useIndex])

  const [rows, setRows] = useState([])
  // Bumped on structural changes (load, add, delete) so the table re-reads `rows`; cell edits made
  // inside the table come back through `setMultiEdit` and need no reload.
  const [structureVersion, setStructureVersion] = useState(0)
  const [viewMode, setViewMode] = useState('table')
  const [tuneOpen, setTuneOpen] = useState(false)
  // PO, 2026-09-17: the table's usual multi-selection (checkbox per row, bulk DELETE in the table's own
  // selection bar, as in every other table). The table's toolbar is hidden until something is checked:
  // it is then shown in its selection mode. Every remount of the table (structural change, search)
  // starts with nothing checked, so does this.
  const [checkedUuids, setCheckedUuids] = useState([])
  const [selectedUuid, setSelectedUuid] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  useEffect(() => { setCheckedUuids([]) }, [structureVersion, searchTerm])
  const [showCatalogPicker, setShowCatalogPicker] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [saveModeOptions, setSaveModeOptions] = useState(null)

  const replaceRows = useCallback((nextRows) => {
    setRows(nextRows)
    setStructureVersion((previous) => previous + 1)
  }, [])

  // PO, 2026-09-15 / 2026-09-17: while the structure is on its way (or the sheet has not told us
  // which version yet) the ROWS show the spinner every MIC table shows on its first load, under the
  // toolbar and the column headers that are already in place. The table is mounted ONCE per version
  // (`bomId` is in its `key`) and its fetch simply waits for the structure: no remount on arrival, so
  // the table's own requests (tune, rows per page, extended columns) are made once, in parallel
  // with the structure request, not again after it.
  const [loaded, setLoaded] = useState(false)
  const structureArrival = useRef(arrival())
  useEffect(() => {
    setLoaded(false)
    structureArrival.current = arrival()
    if (!bomId) return
    const arrived = (nextRows) => { setRows(nextRows); setLoaded(true); structureArrival.current.settle() }
    getBomVersionStructure(bomId)
      .then((data) => arrived((data || []).map(withRowId)), (thrown) => { arrived([]); throw thrown })
  }, [bomId])

  /**
   * H134 S41 / RN-134-17 — the rows go into the tree in NUMERIC position order, so 20 comes before
   * 100.10. Sorting happens HERE, on the library's own rows, and not inside `buildBomPositionTree`:
   * that module belongs to the workflow editor and this story must not touch it.
   */
  const tree = useMemo(() => buildBomPositionTree(sortBomLines(rows)), [rows])

  // S32: the search filters, it does not only highlight. The table lists the matches; the tree
  // keeps each match inside its branch (ancestors visible), which is also what makes the
  // position-based hierarchy of BomTreeView hold together.
  const { matchedUuids, visibleUuids } = useMemo(() => filterStructureBySearch(tree, searchTerm), [tree, searchTerm])
  // PO 2026-09-10: the table lists the components ordered by code by default (the position order
  // is what the tree view is for). Rows sharing a code keep their relative (position) order.
  const tableRows = useMemo(
    () => rows
      .filter((row) => matchedUuids.has(row.uuid))
      .sort((one, two) => compareByCode(one, two)),
    [rows, matchedUuids]
  )
  // BomTreeView's rows (WorkflowResourceTypes) print `remaining` = quantity not yet assigned to a
  // job; a library version has no assignments, so the whole quantity remains.
  const treeItems = useMemo(
    () => rows.filter((row) => visibleUuids.has(row.uuid)).map((row) => ({ ...row, remaining: Number(row.quantity) || 0 })),
    [rows, visibleUuids]
  )

  /**
   * H134 S42 / N23 — how many operations consume each line, matched by `uuid`. It rides ALONGSIDE
   * the structure instead of inside it because `WorkflowResourceDto` is frozen and shared with the
   * editor, the MOS, the fabrication order and Compass (I-134-7).
   *
   * H134 S51 — a failure here is confined to its own column: the map stays empty and the rest of the
   * tree paints normally. Losing one column must not take the whole tab down.
   */
  const [operationsByUuid, setOperationsByUuid] = useState({})
  useEffect(() => {
    let cancelled = false
    setOperationsByUuid({})
    if (!bomId) return undefined
    getBomVersionLineUsage(bomId)
      .then((usage) => {
        if (cancelled) return
        const map = {}
        ;(usage || []).forEach((entry) => { map[entry.componentUuid] = entry.operationCount })
        setOperationsByUuid(map)
      })
      .catch(() => { if (!cancelled) setOperationsByUuid({}) })
    return () => { cancelled = true }
  }, [bomId])

  const findNode = (nodes, uuid) => {
    for (const node of nodes) {
      if (node.uuid === uuid) return node
      const found = findNode(node.children, uuid)
      if (found) return found
    }
    return null
  }
  const selectedNode = selectedUuid ? findNode(tree, selectedUuid) : null

  const findNodeByPosition = (nodes, normalizedPosition) => {
    for (const node of nodes) {
      if (node.normalizedPosition === normalizedPosition) return node
      const found = findNodeByPosition(node.children, normalizedPosition)
      if (found) return found
    }
    return null
  }

  // Each row brings its whole subtree, counted once.
  const subtreeUuidsOf = (rowsToDelete) => {
    const uuids = new Set()
    rowsToDelete.forEach((row) => {
      const node = findNode(tree, row.uuid) || { ...row, children: [] }
      collectSubtreeUuids(node).forEach((uuid) => uuids.add(uuid))
    })
    return uuids
  }

  const removeRows = (uuidsToRemove) => {
    replaceRows(rows.filter((row) => !uuidsToRemove.has(row.uuid)))
    setCheckedUuids([])
  }

  // The row's own icon: confirmation with the total count (S36 of story #117, kept as it was).
  const handleDeleteRequest = (row) => {
    const uuids = subtreeUuidsOf([row])
    setDeleteConfirm({ count: uuids.size, uuids: [...uuids] })
  }

  const handleDeleteConfirm = () => {
    removeRows(new Set(deleteConfirm.uuids))
    setDeleteConfirm(null)
  }

  // The table's bulk DELETE (its selection bar confirms the checked rows first). For a table that is
  // not pageable the toolbar hands over the checked rows themselves, as an array. Local only: the
  // lines leave with Save, like every other edit of the structure.
  const deleteCheckedRows = (checkedRows) => {
    removeRows(subtreeUuidsOf(checkedRows))
    return Promise.resolve()
  }

  const handleAddFromCatalog = (parentPosition) => (row, resourceType) => {
    // The new component goes after the last sibling of the chosen parent (or at root level), in
    // steps of 10 like the imported files; siblings are counted on the normalized tree.
    const parentNode = parentPosition ? findNodeByPosition(tree, parentPosition) : null
    const siblingCount = parentNode ? parentNode.children.length : tree.length
    const newPosition = parentPosition ? parentPosition + '.' + ((siblingCount + 1) * 10) : String((siblingCount + 1) * 10)
    // Every field Sun requires (`BomComponentInputDto`) starts present: the user completes the
    // engineering identity and the rest in the table before saving.
    const uuid = 'new-' + Date.now() + '-' + Math.random()
    replaceRows([...rows, {
      uuid,
      id: uuid,
      resource: { code: row.code, name: row.name },
      resourceType,
      resourceTypeName: resourceType,
      position: newPosition,
      quantity: 1,
      engItem: '',
      engRevision: '',
      revision: '',
      category: '',
      componentType: ''
    }])
    setShowCatalogPicker(null)
  }

  // The table only ever sees the rows matching the search: its edits are merged back by `id` into
  // the full list so the hidden rows are never dropped.
  const rowKey = (row) => (row.id === undefined || row.id === null ? row.uuid : row.id)
  const handleMultiEdit = (editedRows) => {
    const editedByKey = new Map((editedRows || []).map((row) => [rowKey(row), row]))
    setRows((previous) => previous.map((row) => editedByKey.get(rowKey(row)) || row))
  }

  const performSave = async (saveMode, indexValue) => {
    const components = buildBomVersionSaveRequestComponents(rows)
    // Without the index dimension a new version is born APPROVED without asking (RN-G20); an
    // overwrite keeps the index the edited version already carries, exactly as it does today.
    const indexValueWithoutIndex = saveMode === 'OVERWRITE'
      ? (version.indexValue || DEFAULT_INDEX_VALUE_WITHOUT_INDEX)
      : DEFAULT_INDEX_VALUE_WITHOUT_INDEX
    const request = {
      saveMode,
      indexValue: useIndex ? (indexValue || version.indexValue || '') : indexValueWithoutIndex,
      name: version.name || '',
      description: version.description || '',
      components
    }
    try {
      const result = await saveBomVersion(bomId, request)
      setSaveModeOptions(null)
      setSaveError('')
      showAlert({ severity: 'success', title: t_('Saved correctly'), message: '' })
      onSaved(result)
    } catch (thrown) {
      // E-BOM-10 (RN-04.7): conditions changed between N5 and N9 — offer the modes Sun allows now
      const { error, context } = readBomLibraryError(thrown)
      const literal = error || t_('Not allowed')
      const allowedSaveModes = Array.isArray(context.allowedSaveModes) && context.allowedSaveModes.length > 0 ? context.allowedSaveModes : null
      setSaveError(literal)
      setSaveModeOptions(allowedSaveModes)
      // Any other rejection (403 without body, catalog/position errors...) has no dialog to reopen:
      // it is reported at the top of the section like the validation refusal.
      if (!allowedSaveModes) showAlert({ severity: 'error', title: translateBomLibraryError(t_, literal), message: '' })
    }
  }

  const handleSave = async () => {
    // Sun's BomComponentInputDto rejects null resourceCode/resourceType/position/quantity/engItem/
    // engRevision: refuse the save here, naming the components still incomplete, instead of
    // letting the round trip fail.
    const missing = findRowsMissingRequiredFields(rows)
    if (missing.length > 0) {
      setSaveModeOptions(null)
      showAlert({
        severity: 'error',
        title: t_('Required fields are missing in: {{components}}', {
          components: missing.map((entry) => (entry.resourceCode || entry.uuid) + ' (' + entry.fields.join(', ') + ')').join('; ')
        }),
        message: ''
      })
      return
    }
    const options = await getBomVersionSaveMode(bomId)
    setSaveError('')
    setSaveModeOptions(options.allowedSaveModes || [])
  }

  // Same column set as the workflow editor's BOM table (`workflow2.0/WorkflowResource.jsx`), minus
  // the job-assignment columns that only make sense inside a workflow. Code, name and type come from
  // the catalog pick and are never typed by hand (S33); everything else is editable with
  // BOM_LIBRARY_EDIT. Mandatory = the non-nullable fields of Sun's `BomComponentInputDto`.
  const columns = [
    { field: 'engItem', name: t_('Engineering item'), mandatory: true },
    { field: 'engRevision', name: t_('Engineering revision'), mandatory: true },
    {
      field: 'resourceTypeName',
      name: t_('Type'),
      readOnly: true,
      /**
       * H134 S44 / D10 — the type is DERIVED: a line with descendants is an Assembly, a leaf on the
       * raw-material catalogue is Raw material, and everything else is Commercial.
       *
       * ⚠️ H134 S45 — "Commercial" is a LABEL, not a fact about the part: manufactured parts land in
       * that bucket too, because nothing in the system tells a bought part from a made one. Signed
       * knowing it; there is no fourth type.
       */
      render: (row) => t_(bomLineTypeLabelKey(row, rows))
    },
    { field: 'resource.code', name: t_('Code'), readOnly: true, render: (row) => resourceCodeOf(row) },
    { field: 'resource.name', name: t_('Name'), readOnly: true, render: (row) => resourceNameOf(row) },
    {
      field: 'position',
      name: t_('position'),
      mandatory: true,
      // H134 S46 - the position is monospaced and NOT indented: the indentation is the tree's job,
      // and a proportional font makes dotted positions impossible to line up by eye.
      render: (row) => <span style={{ fontFamily: 'monospace' }}>{row.position}</span>
    },
    { field: 'revision', name: t_('Revision') },
    { field: 'category', name: t_('Category') },
    { field: 'componentType', name: t_('Component type') },
    { field: 'quantity', name: t_('Quantity'), numeric: true, mandatory: true },
    {
      // H134 S42 — "{{count}} op". Zero is a datum (I-134-2), so a line nobody consumes shows 0 op;
      // a line the fetch could not resolve shows nothing at all (H134 S51).
      field: 'operationCount',
      name: t_('Operations'),
      readOnly: true,
      render: (row) => {
        const count = operationsByUuid[row.uuid]
        return count === undefined ? '' : t_('{{count}} op').replace('{{count}}', count)
      }
    }
  ]

  const getColumns = () => withExtendedColumns(columns)

  const tableActions = canEdit
    ? {
        // Bulk delete of the checked rows, offered by the table's selection bar. Hidden per row: the
        // row keeps its direct icon below and no kebab menu (ActionsMenu renders nothing).
        delete: { fetchFunction: deleteCheckedRows, visibilityButton: () => false },
        custom: [{
          name: 'Delete',
          // PO, 2026-09-17: the only action a line has is its icon, right there — not a kebab menu with
          // one entry. `contextMenu: false` makes the table paint `render` directly, so the click is ours.
          contextMenu: false,
          render: (row) => (
            <IconButton
              size='small'
              title={t_('Delete')}
              onClick={(event) => { event.stopPropagation(); handleDeleteRequest(row) }}
            >
              <DeleteOutline />
            </IconButton>
          )
        }]
      }
    : undefined

  // The table asks once on mount (and on each of its refresh ticks): it gets the rows as soon as the
  // structure has arrived, read at that moment so a fetch issued before the load sees the real rows.
  const latestTableRows = useRef(tableRows)
  latestTableRows.current = tableRows
  const dataFetch = () => structureArrival.current.promise.then(() => latestTableRows.current)

  /**
   * H134 S47 / D4 — the footer: a FLAT sum of every line, without multiplying a child by its parent
   * and without grouping by unit.
   *
   * ⚠️ H134 S48 — this total is NOT the denominator of the coverage shown in the Usage tab. That one
   * counts only the LEAVES (D5). Two different numbers, on purpose: presenting them as the same
   * thing is exactly the confusion the scenario exists to prevent.
   */
  const footer = useMemo(() => bomLinesFooter(rows), [rows])

  return (
    // Fills the height its host gives it; the feedback, the toolbar and the selection stats stay
    // fixed and only the table / tree below scrolls (PO 2026-09-10).
    <Stack spacing={2} data-testid='bom-version-structure' sx={{ textAlign: 'left', height: '100%', minHeight: 0 }}>
      {AlertElement}
      {/* H134 S50 (PO, 2026-09-17): a version with no lines shows the table's own "no data" and nothing
          else — a message of ours on top was redundant. */}
      <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
        <TextField
          label={t_('Search component')}
          size='small'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <Stack direction='row' spacing={1} alignItems='center'>
          {canEdit && (
            <Button onClick={() => setShowCatalogPicker(selectedNode ? selectedNode.normalizedPosition : '')}>
              {t_('Add from catalog')}
            </Button>
          )}
          {canEdit && <Button onClick={handleSave}>{t_('Save')}</Button>}
          {/* PO, 2026-09-17: the table's TUNE, but in THIS bar next to the view toggle, not in the
              generic toolbar (which stays hidden). Same look as the button every MIC table shows. */}
          {viewMode === 'table' && (
            <Button
              size='small'
              variant='outlined'
              startIcon={<TuneOutlined />}
              sx={{ borderColor: 'transparent' }}
              data-testid='bom-structure-tune'
              onClick={() => setTuneOpen(true)}
            >
              {t_('TUNE')}
            </Button>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size='small'
            onChange={(event, value) => { if (value) setViewMode(value) }}
          >
            <ToggleButton value='tree' data-testid='bom-view-toggle-tree'>{t_('Tree view')}</ToggleButton>
            <ToggleButton value='table' data-testid='bom-view-toggle-table'>{t_('Table view')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* PO, 2026-09-15: the figures of the selection and of the footer as stat chips — bold tabular
          number, muted label — instead of bare text. Same chip language as the sheet header. */}
      {selectedNode && (
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap data-testid='bom-structure-selection-stats'>
          <Chip size='small' color='primary' variant='outlined' label={resourceCodeOf(selectedNode) || selectedNode.position} sx={{ fontFamily: 'monospace' }} />
          <StatChip value={countDescendants(selectedNode)} label={t_('components')} />
          <StatChip value={countLevelsBelow(selectedNode)} label={t_('Levels below')} />
        </Stack>
      )}

      {/* H134 S47 / S74 - the footer, with tabular figures so columns of digits line up. */}
      {rows.length > 0 && (
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap data-testid='bom-structure-footer' sx={{ fontVariantNumeric: 'tabular-nums' }}>
          <StatChip value={footer.lines} label={t_('Lines')} />
          <StatChip value={footer.assemblies} label={t_('Assemblies')} />
          <StatChip value={footer.units} label={t_('Units')} />
        </Stack>
      )}

      {viewMode === 'table'
        ? (
          <Box data-testid='bom-structure-table' sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {/* Remounted on every structural change and on every search: in multi-edit mode the
                table copies the rows into its own editing state only on its FIRST load
                (SmartMateriaUITable `setDataForMultipleEdit` under `!firstLoad`), so a plain
                `forceLoad` would leave an added, deleted or filtered component invisible. Cell edits
                are already in `rows` through `setMultiEdit`, so nothing is lost by remounting. */}
            {/* PO, 2026-09-17: the table keeps its own TUNE (column chooser, remembered per user under
                `bomLibraryLines`); its dialog is opened from the section's bar above, so the generic
                toolbar stays hidden and the section's "Search component" field is the only search. */}
            <SmartMateriaUITable
              key={bomId + '#' + structureVersion + '#' + searchTerm}
              columns={getColumns()}
              dataFetch={dataFetch}
              sortOptions={DEFAULT_TABLE_SORT_OPTIONS}
              setMultiEdit={canEdit ? handleMultiEdit : undefined}
              actions={tableActions}
              primaryKeys={['uuid']}
              onRowClick={(row) => setSelectedUuid(row.uuid)}
              multipleSelection={canEdit}
              multipleSelectionChange={(selection) => setCheckedUuids(selection.rows.map((row) => row.uuid))}
              tableTuneUniqueKey='bomLibraryLines'
              tuneOpen={tuneOpen}
              onTuneClose={() => setTuneOpen(false)}
              searchAndPaginationVisible={checkedUuids.length > 0 ? undefined : 'none'}
              tablePageSize={Number.MAX_SAFE_INTEGER}
            />
          </Box>
          )
        : (
          <Box data-testid='bom-structure-tree' sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {/* The section's own "Search component" field already filters `treeItems`. */}
            {loaded
              ? <BomTreeView items={treeItems} showJobChips={false} visibleSearch={false} />
              : <Box sx={{ py: 4, textAlign: 'center' }} data-testid='bom-structure-loading'><CircularProgress /></Box>}
          </Box>
          )}

      {showCatalogPicker !== null && (
        <CatalogComponentPicker
          onClose={() => setShowCatalogPicker(null)}
          onSelect={handleAddFromCatalog(showCatalogPicker)}
        />
      )}

      <SmartDialog
        setOpen={Boolean(deleteConfirm)}
        title='default'
        message={deleteConfirm ? deleteConfirm.count + ' ' + t_('components will be deleted') : ''}
        acceptLabel='Delete'
        acceptCallback={handleDeleteConfirm}
        cancelCallback={() => setDeleteConfirm(null)}
        autoClose={false}
      />

      {saveModeOptions && (
        <BomSaveModeDialog
          key={saveModeOptions.join(',') + saveError}
          allowedSaveModes={saveModeOptions}
          indexOptions={indexOptions}
          useIndex={useIndex}
          defaultIndexValue={version.indexValue || ''}
          error={saveError}
          onClose={() => { setSaveModeOptions(null); setSaveError('') }}
          onConfirm={performSave}
        />
      )}
    </Stack>
  )
}

export default BomVersionStructure
